import React, { useState } from 'react';
import { useStudio, TEMPLATES } from '@/lib/store';
import { Hexagon, Square, Triangle, Diamond, Circle, Pentagon, Grip, Sparkles, Lock } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import UpgradeDialog from '@/components/studio/UpgradeDialog';

const ICONS = {
  trionda: Sparkles,
  cube: Square,
  brazuca: Grip,
  octahedron: Diamond,
  dodecahedron: Pentagon,
  dodecahedron_plus: Grip,
  dodecahedron_ultra: Grip,
  truncated_octahedron: Grip,
  icosahedron: Triangle,
  icosahedron_plus: Grip,
  truncated_icosahedron: Hexagon,
  truncated_icosahedron_plus: Grip,
};

export default function LeftRail() {
  const templateId = useStudio((s) => s.templateId);
  const setTemplate = useStudio((s) => s.setTemplate);
  const { isPro } = useAuth();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [previewId, setPreviewId] = useState(null);

  return (
    <div className="w-full flex flex-col p-5 gap-5 overflow-y-auto" data-testid="left-rail-content">
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={(v) => { setUpgradeOpen(v); if (!v) setPreviewId(null); }}
        previewTemplateId={previewId}
      />
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2">
          How it works
        </div>
        <p className="text-xs leading-relaxed text-slate-600">
          Pick a starting shape, then grab any blue vertex on the ball. Every adjacent panel shares
          that point — moving it re-shapes all touching panels so they always interlock perfectly.
        </p>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-3">
          Templates
        </div>
        <div className="grid grid-cols-1 gap-2">
          {TEMPLATES.map((t) => {
            const Icon = ICONS[t.id] || Hexagon;
            const active = t.id === templateId;
            const locked = t.pro && !isPro;
            const handleClick = () => {
              if (locked) { setPreviewId(t.id); setUpgradeOpen(true); }
              else setTemplate(t.id);
            };
            return (
              <button
                key={t.id}
                onClick={handleClick}
                data-testid={`template-card-${t.id}`}
                data-locked={locked ? 'true' : 'false'}
                className={`group flex items-center gap-3 rounded-2xl p-3 text-left transition-colors duration-200 border ${
                  active
                    ? 'border-[#007AFF] bg-blue-50/70 shadow-[0_4px_18px_rgba(0,122,255,0.12)]'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    active ? 'bg-[#007AFF] text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                    {t.name}
                    {locked && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-semibold px-1.5 py-0.5 uppercase tracking-wider"
                        data-testid={`template-pro-badge-${t.id}`}
                      >
                        <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
                        Pro
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{t.description}</div>
                </div>
                <div className="ml-auto font-mono text-[11px] text-slate-400">{t.panels}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
